/**
 * @fileoverview useTemplatePreview - React hook for generating template previews.
 *
 * This hook takes a template ID (PDF or DOCX) and returns a ready-to-render
 * PDF blob along with loading and error state. The internal pipeline differs
 * based on the template type:
 *
 * PDF Workflow:
 * 1. Fetch full template to get the file URL and field metadata.
 * 2. Download the original PDF file from S3.
 * 3. Call the **normalize** endpoint to deskew/flatten the PDF server-side.
 * 4. Annotate the normalized PDF locally with real matter context data so users see filled fields.
 *
 * DOCX Workflow:
 * 1. Fetch full template to get field metadata.
 * 2. Fetch real matter context data for the given matterId.
 * 3. Render the DOCX to PDF via the v2 API (server handles file fetch, merge, and conversion).
 * 4. Normalize the generated PDF server-side.
 *
 * The hook consolidates these asynchronous steps into a single `isLoading`
 * boolean so consuming components can show one spinner. It also exposes any
 * errors encountered during the pipeline.
 */

import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useMemo, useState } from 'react';

import { useGetMatterFlattenedContextQuery } from '@/services/api/matterFlattenedContextService';
import { templatesV2EnhancedApi } from '@/services/api/templatesV2ServiceEnhanced';
import { store } from '@/state/state';

import { modifyPdf } from '../pdf/renderer/utils';
import { PdfFormTemplateField } from '../types';
import {
  computeDocxMergeFields,
  computePdfMergeFields,
  DocxMergeFieldValue,
} from './mergeFields';
import { useGetTemplate } from './useGetTemplate';

/**
 * Result object returned by `useTemplatePreview`.
 */
interface UseTemplatePreviewResult {
  /** Final PDF blob ready for rendering (null until ready) */
  previewBlob: Blob | null;
  /** For PDF templates: array of placed fields used for annotation */
  placedFields?: PdfFormTemplateField[];
  /** Original template type ('PDF' | 'DOCX') */
  templateType: 'PDF' | 'DOCX' | string;
  /** Consolidated loading state covering fetch + post-processing */
  isLoading: boolean;
  /** Error object if any step in the pipeline failed */
  error: unknown | null;
}

/**
 * React hook that fetches a template (PDF or DOCX) and produces a preview PDF blob.
 *
 * @param templateId - ID of the template to preview. Pass null/undefined to skip.
 * @param matterId - ID of the matter to get context data from. Pass null/undefined to skip.
 * @param context - Matter context which may include arrays for Jinja2 iteration
 * @param overrides - Field overrides that take precedence over matter context
 * @returns An object containing the preview blob, loading and error states.
 */
export const useTemplatePreview = (
  templateId: string | null | undefined,
  matterId?: string | null | undefined,
  context?: Record<string, DocxMergeFieldValue | undefined>,
  overrides: Record<string, string> = {},
  medicalNoteId?: string,
): UseTemplatePreviewResult => {
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [placedFields, setPlacedFields] = useState<
    PdfFormTemplateField[] | undefined
  >(undefined);
  const [error, setError] = useState<unknown | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { template } = useGetTemplate(templateId);

  // Debounce overrides to prevent rapid re-renders
  const debouncedOverrides = useDebounce(overrides, 300);

  // Fetch real matter context data ONLY if matterId is provided AND no valid context was passed
  // This prevents duplicate fetching when parent already provides context
  const hasValidContext = context && Object.keys(context).length > 0;
  const shouldFetchContext = Boolean(matterId && !hasValidContext);

  const {
    data: matterContext,
    isLoading: isLoadingContext,
    error: contextError,
  } = useGetMatterFlattenedContextQuery(
    { matterId: matterId || '', medicalNoteId },
    { skip: !shouldFetchContext },
  );

  // Use passed context if available, otherwise use fetched context
  // This ensures we don't merge potentially stale data
  const finalContext = useMemo(() => {
    if (hasValidContext) {
      return context;
    }
    return matterContext || {};
  }, [hasValidContext, context, matterContext]);

  // Consolidated async pipeline
  useEffect(() => {
    const run = async () => {
      if (!template) return;

      // If we're fetching context and it's still loading or errored, wait
      if (shouldFetchContext && (isLoadingContext || contextError)) return;

      // Don't run if we have an empty context (still loading from parent)
      if (!hasValidContext && !matterContext) return;

      setError(null);
      setIsProcessing(true);
      try {
        // V2 templates use `format` instead of `type`, and `file_url` instead of `pdf_url`/`docx_url`
        if (template.format === 'PDF') {
          if (!template.file_url) {
            throw new Error('PDF template has no file URL');
          }
          // 1️⃣ Download original PDF
          const pdfResp = await fetch(template.file_url);
          const originalPdf = await pdfResp.blob();

          // 2️⃣ Normalize via backend (deskews, flattens, etc.)
          const normalizedResult = await store
            .dispatch(
              templatesV2EnhancedApi.endpoints.templateNormalizePdf.initiate(
                {
                  file: originalPdf,
                },
              ),
            )
            .unwrap();

          const mergeFields = computePdfMergeFields(
            template.fields ?? [],
            finalContext as Record<string, string | undefined>,
            debouncedOverrides,
          );
          // 3️⃣ Annotate with real matter context data so users see fields filled
          // Note: modifyPdf expects legacy PdfFormTemplateField[] - cast v2 fields
          // since the position data is structurally compatible (pdf_position vs position)
          const finalPdf = await modifyPdf({
            pdfBlob: normalizedResult as Blob,
            placedFields: (template.fields ?? []) as unknown as PdfFormTemplateField[],
            mergeData: mergeFields,
          });

          setPlacedFields((template.fields ?? []) as unknown as PdfFormTemplateField[]);
          setPreviewBlob(finalPdf);
        } else if (template.format === 'DOCX') {
          const mergeFields = computeDocxMergeFields(
            template.fields ?? [],
            finalContext as Record<string, DocxMergeFieldValue | undefined>,
            debouncedOverrides,
          );

          // 1️⃣ Render to PDF via v2 API (server handles DOCX fetch + merge + conversion)
          const pdfBlob = await store
            .dispatch(
              templatesV2EnhancedApi.endpoints.templateRenderToPdf.initiate({
                id: template.id,
                fields: mergeFields,
                matterId: matterId ?? null,
              }),
            )
            .unwrap();

          // 2️⃣ Normalize the generated PDF to ensure compatibility for rendering
          const normalizedPdfBlob = await store
            .dispatch(
              templatesV2EnhancedApi.endpoints.templateNormalizePdf.initiate(
                {
                  file: pdfBlob as Blob,
                },
              ),
            )
            .unwrap();

          setPreviewBlob(normalizedPdfBlob as Blob);
        } else {
          throw new Error(`Unsupported template format: ${template.format}`);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Template preview pipeline failed', e);
        setError(e);
      } finally {
        setIsProcessing(false);
      }
    };

    run();
    // We only care when the *id* or *type* changes; suppress exhaustive deps for store, api.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    templateId,
    template,
    shouldFetchContext,
    hasValidContext,
    matterContext,
    finalContext,
    debouncedOverrides,
  ]);

  return {
    previewBlob,
    placedFields,
    templateType: template?.format ?? '',
    isLoading: isProcessing || isLoadingContext,
    error: error || contextError,
  };
};
